"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3 } from "lucide-react";
import { Component, type ReactNode } from "react";
import { formatUSPhoneNumber } from "@/shared/utils/phone-utils";
import { FileViewer } from "@/src/components/file-viewer/file-viewer";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";
import { useErrorLogs } from "@/src/lib/data-fetchers/error-log-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { buildOutboundFaxFilename, buildOutboundFaxGcsPath } from "@/src/lib/gcs-path";
import { replyFaxPdfOutputSchema, replyFaxRoutingOutputSchema } from "@/src/models/reply-fax";
import { TaskStatus } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
class FaxPdfPreviewBoundary extends Component<{
  children: ReactNode;
}, {
  failed: boolean;
}> {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  render() {
    return this.state.failed ? <p role="alert" className="p-4 text-sm">
        The fax PDF preview could not load. Refresh to try again.
      </p> : this.props.children;
  }
}
export type DocumentWorkflowStepDefinition = {
  key: string;
  title: string;
  description: string;
};
export function DocumentWorkflowSteps(t0) {
  const $ = _c(30);
  const {
    taskId,
    status,
    definitions,
    taskSteps
  } = t0;
  const t1 = status === TaskStatus.FAILED;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    errorLogs
  } = useErrorLogs(taskId, t2);
  let t3;
  if ($[2] !== errorLogs) {
    t3 = errorLogs.toSorted(_temp)[0]?.errorMessage;
    $[2] = errorLogs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const latestError = t3;
  let t4;
  if ($[4] !== definitions || $[5] !== taskSteps) {
    let t5;
    if ($[7] !== taskSteps) {
      t5 = definition => taskSteps.find(step => step.stepKey === definition.key)?.status !== TaskStepStatus.COMPLETED;
      $[7] = taskSteps;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = definitions.findIndex(t5);
    $[4] = definitions;
    $[5] = taskSteps;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const firstUnfinished = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== definitions || $[10] !== firstUnfinished || $[11] !== latestError || $[12] !== status || $[13] !== taskId || $[14] !== taskSteps) {
    const generated = taskSteps.find(_temp2);
    const packet = replyFaxPdfOutputSchema.safeParse(generated?.output);
    const submitted = taskSteps.some(_temp3);
    const filename = buildOutboundFaxFilename(taskId);
    const pdfPath = submitted ? buildOutboundFaxGcsPath(taskId, filename) : packet.success ? packet.data.pathToFile : null;
    t5 = "space-y-4";
    if ($[20] !== status) {
      t6 = status === TaskStatus.AWAITING_INFORMATION && <p className="text-sm text-muted-foreground">Awaiting an updated document. Fax submission does not confirm delivery.</p>;
      $[20] = status;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    t7 = definitions.map((definition_0, index) => {
      const step_2 = taskSteps.find(candidate => candidate.stepKey === definition_0.key);
      const failed = step_2?.status === TaskStepStatus.FAILED;
      const completed = step_2?.status === TaskStepStatus.COMPLETED;
      const skipped = completed && step_2.output?.outcome === "skipped";
      const waiting = step_2?.status === TaskStepStatus.ASYNC || step_2?.status === TaskStepStatus.PAUSED;
      const running = !step_2 && status === TaskStatus.PROCESSING && index === firstUnfinished;
      const label = failed ? "Failed" : skipped ? "Not needed" : completed ? step_2.output?.outcome === "submitted" ? "Submitted" : "Completed" : waiting ? "Waiting" : running ? "In progress" : "Not run";
      const routing = replyFaxRoutingOutputSchema.safeParse(step_2?.output);
      return <ValerieCard key={definition_0.key} title={`${index + 1}. ${definition_0.title}`} icon={failed ? <CircleAlert /> : completed ? <CheckCircle2 /> : waiting || running ? <Clock3 /> : <CircleDashed />} variant={failed ? "destructive" : completed && !skipped ? "success" : "outline"} actions={<Badge variant="outline">{label}</Badge>} contentClassName="space-y-2 pt-4"><p className="text-sm text-muted-foreground">{definition_0.description}</p>{routing.success && <p className="text-sm">Fax to: {formatUSPhoneNumber(routing.data.targetFaxNumber)}</p>}{definition_0.key === "build_reply_fax" && packet.success && <p className="text-sm">{packet.data.pageCount} pages generated. Preview below.</p>}{failed && <p role="alert" className="text-sm text-destructive">{latestError || "This step failed. Later steps have not run."}</p>}</ValerieCard>;
    });
    if ($[22] !== definitions.length) {
      t8 = definitions.length === 0 && <p className="text-sm text-muted-foreground">No workflow steps are configured for this task.</p>;
      $[22] = definitions.length;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t9 = pdfPath && <ValerieCard title="Fax PDF" contentClassName="h-[900px] p-0"><FaxPdfPreviewBoundary key={pdfPath}><FileViewer url={fileViewUrl(pdfPath)} filename={filename} contentType="application/pdf" /></FaxPdfPreviewBoundary></ValerieCard>;
    $[9] = definitions;
    $[10] = firstUnfinished;
    $[11] = latestError;
    $[12] = status;
    $[13] = taskId;
    $[14] = taskSteps;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t10 = <div className={t5}>{t6}{t7}{t8}{t9}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp3(step_1) {
  return step_1.status === TaskStepStatus.COMPLETED && step_1.output?.outcome === "submitted";
}
function _temp2(step_0) {
  return step_0.stepKey === "build_reply_fax" && step_0.status === TaskStepStatus.COMPLETED;
}
function _temp(a, b) {
  return b.createdAt.localeCompare(a.createdAt);
}